import { getFormattedCaretPosition } from '@shared/ui/formatted-caret';

export type InputMask = 'number' | 'time' | 'date' | 'money' | false;
export type CaretPreservingInputMask = Extract<InputMask, 'number' | 'date' | 'money'>;

interface FormatInputValueOptions {
  mask: InputMask;
  firstLetterUpperCase: boolean;
  upperCase: boolean;
  lowerCase: boolean;
  maxLength: number | boolean;
}

const firstLetterToUpperCase = (value: string): string => {
  if (!value || value.length === 0) {
    return '';
  }

  return value[0]!.toUpperCase() + value.slice(1);
};

const applyMoneyMask = (value: string): string => {
  let formattedValue = '';
  let hasDecimalSeparator = false;
  let decimalDigitsCount = 0;

  for (const character of value) {
    if (/\d/.test(character)) {
      if (!hasDecimalSeparator || decimalDigitsCount < 2) {
        formattedValue += character;
      }

      if (hasDecimalSeparator) {
        decimalDigitsCount += 1;
      }

      continue;
    }

    if ((character === ',' || character === '.') && formattedValue.length > 0 && !hasDecimalSeparator) {
      formattedValue += ',';
      hasDecimalSeparator = true;
    }
  }

  return formattedValue;
};

/**
 * Цифры даты по группам дд.мм.гггг, как время: точка встаёт перед следующей цифрой, поэтому стирание
 * не упирается в точку. Одиночная цифра перед только что набранной точкой дополняется нулём: «1.» → «01».
 * Диапазоны не проверяются: несуществующую дату ловит валидация формы.
 */
const applyDateMask = (value: string): string => {
  const groups = value.replace(/[^\d.]/g, '').split('.');
  const digits = groups
    .map((group, index) =>
      group.length === 1 && index === groups.length - 2 && groups.at(-1) === '' ? `0${group}` : group,
    )
    .join('')
    .slice(0, 8);

  return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4)].filter(Boolean).join('.');
};

export const applyMask = (mask: InputMask, value: string): string => {
  switch (mask) {
    case 'number':
      return value.replace(/[^0-9]+/g, '');

    case 'time': {
      const digits = value.replace(/\D/g, '');

      return digits.length <= 2 ? digits : `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
    }

    case 'date':
      return applyDateMask(value);

    case 'money':
      return applyMoneyMask(value);

    default:
      return value;
  }
};

export const getMaskedCaretPosition = (
  mask: CaretPreservingInputMask,
  rawValue: string,
  formattedValue: string,
  rawCaretPosition: number,
): number => getFormattedCaretPosition((value) => applyMask(mask, value), rawValue, formattedValue, rawCaretPosition);

export const formatInputValue = (value: string, options: FormatInputValueOptions): string => {
  let formattedValue = applyMask(options.mask, value);

  if (options.firstLetterUpperCase && formattedValue.length > 0) {
    formattedValue = firstLetterToUpperCase(formattedValue);
  } else if (options.upperCase) {
    formattedValue = formattedValue.toUpperCase();
  } else if (options.lowerCase) {
    formattedValue = formattedValue.toLowerCase();
  }

  if (options.maxLength && typeof options.maxLength === 'number') {
    formattedValue = formattedValue.substring(0, options.maxLength);
  }

  return formattedValue;
};
